import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Layout,
    Menu,
    theme,
    Avatar,
    Space,
    Typography,
    Popover,
    Button,
    message,
    Progress,
    Modal,
    Drawer,
} from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    UserOutlined,
    LogoutOutlined,
    QuestionCircleOutlined,
    LeftOutlined,
    RightOutlined,
    CrownFilled,
    StarFilled,
    RocketFilled,
    MenuOutlined,
} from '@ant-design/icons';
import AuthService from '../../services/authService';
import { useQuotaStatus } from '../../hooks/useQuotaStatus';
import { QuotaBadge } from '../QuotaBadge';
import { toCanonicalPath } from '../../utils/menuPaths';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'tsad_main_sidebar_collapsed';

function readStoredSidebarCollapsed() {
    if (typeof window === 'undefined') return false;
    try {
        return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === '1';
    } catch {
        return false;
    }
}

const PolicySection = ({ title, children }) => (
    <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 6, paddingBottom: 4, borderBottom: '1px solid #f1f5f9' }}>{title}</div>
        <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.85 }}>{children}</div>
    </div>
);

const Divider = () => (
    <span style={{ width: 1, height: 10, background: '#dde1e7', margin: '0 12px', flexShrink: 0, display: 'inline-block' }} />
);
const Item = ({ label, value }) => (
    <span style={{ fontSize: 10, color: '#64748b', whiteSpace: 'nowrap' }}>
        <span style={{ color: '#94a3b8', marginRight: 4 }}>{label}</span>
        <span style={{ fontWeight: 500 }}>{value}</span>
    </span>
);

const MainLayout = () => {
    const [collapsed, setCollapsed] = useState(readStoredSidebarCollapsed);
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 991 : false);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [menuItems, setMenuItems] = useState([]);
    const [menuLabelByPath, setMenuLabelByPath] = useState({});
    const [features, setFeatures] = useState([]);
    const [allQuotas, setAllQuotas] = useState({});
    const [allRowLimits, setAllRowLimits] = useState({});
    const navigate = useNavigate();
    const location = useLocation();
    const { token } = theme.useToken();

    const [currentUser, setCurrentUser] = useState(AuthService.getUserInfo());
    const [policyModal, setPolicyModal] = useState(null); // 'terms' | 'privacy' | 'payment'
    const [domainModal, setDomainModal] = useState(() => window.location.hostname.includes('adhelp.store'));

    useEffect(() => {
        const handleUserUpdate = () => setCurrentUser(AuthService.getUserInfo());
        window.addEventListener('user_info_updated', handleUserUpdate);
        return () => window.removeEventListener('user_info_updated', handleUserUpdate);
    }, []);

    useEffect(() => {
        if (!AuthService.getRefreshToken()) return;
        AuthService.refreshAccessToken()
            .then(() => window.dispatchEvent(new Event('user_info_updated')))
            .catch(() => { /* 갱신 실패 시 무시 */ });
    }, [location.pathname]);

    const uname = currentUser?.name || '사용자';
    const grade = currentUser?.grade?.toUpperCase() || 'USER';
    const subscriptionEndDate =
        currentUser?.subscription_end_date ?? currentUser?.subscriptionEndDate ?? null;

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 991;
            setIsMobile(mobile);
            if (mobile) setCollapsed(true);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, collapsed ? '1' : '0');
        } catch {
            /* 저장 불가(비공개 모드 등) 시 무시 */
        }
    }, [collapsed]);

    useEffect(() => {
        AuthService.ensureSessionValidatedOnAppEntry();
        message.config({
            top: 8,
            duration: 2,
            maxCount: 1,
        });
    }, []);

    useEffect(() => {
        AuthService.authAxios
            .get('/settings/features')
            .then(({ data }) => setFeatures(data?.data || []))
            .catch(() => {});
    }, []);

    const fetchAllQuotas = useCallback(async () => {
        try {
            const { data } = await AuthService.authAxios.get('/naver/quota/status');
            if (data?.ok) setAllQuotas(data.quotas || {});
        } catch {
            // 실패 시 무시
        }
    }, []);

    const fetchAllRowLimits = useCallback(async () => {
        try {
            const { data } = await AuthService.authAxios.get('/settings/row-limits');
            if (data?.ok) {
                const limits = data.rowLimits || {};
                const lowerLimits = {};
                for (const k in limits) lowerLimits[k.toLowerCase()] = limits[k];
                setAllRowLimits(lowerLimits);
            }
        } catch {
            // 실패 시 무시
        }
    }, []);

    useEffect(() => {
        fetchAllQuotas();
        fetchAllRowLimits();
    }, [fetchAllQuotas, fetchAllRowLimits]);

    useEffect(() => {
        if (features.length === 0) return;
        const handler = () => fetchAllQuotas();
        const types = features.map(f => f.queryType);
        types.forEach(t => window.addEventListener(`${t}QuotaUpdated`, handler));
        return () => types.forEach(t => window.removeEventListener(`${t}QuotaUpdated`, handler));
    }, [features, fetchAllQuotas]);

    const findMid = useQuotaStatus('findMid', '/findMid');
    const findProductByMid = useQuotaStatus('findProductByMid', '/findProductByMid');
    const keywordVer1 = useQuotaStatus('keywordVer1', '/keywordVer1');
    const keyword1 = useQuotaStatus('keyword1', '/keywordVer1');
    const naverIntegratedRank = useQuotaStatus('naverIntegratedRank', '/NaverIntegratedRank');
    const countText = useQuotaStatus('countText', '/countText');
    const keywordVer2 = useQuotaStatus('keywordVer2', '/keywordVer2');
    const keywordRankVolumeCheck = useQuotaStatus(
        'keywordRankVolumeCheck',
        '/KeywordRankVolumeCheck'
    );
    const findNaverRank = useQuotaStatus('findNaverRank', '/findNaverRank');
    const findNaverRankTracking = useQuotaStatus('findNaverRankTracking', '/findNaverRankTracking');
    const integratedSearch = useQuotaStatus('integratedSearch', '/integratedSearch');
    const productAnalysis = useQuotaStatus('productAnalysis', '/product-analysis');
    const rankHistoryWatch = useQuotaStatus('rankHistoryWatch', '/rankHistoryWatch');
    const autoComplete = useQuotaStatus('autoComplete', '/autoComplete');
    const productGenerate = useQuotaStatus('productGenerate', '/productGenerate');
    const changeUrl = useQuotaStatus('changeUrl', '/changeUrl');

    const quotaByKey = {
        findMid,
        findProductByMid,
        keywordVer1,
        keyword1,
        naverIntegratedRank,
        countText,
        keywordVer2,
        keywordRankVolumeCheck,
        findNaverRank,
        findNaverRankTracking,
        integratedSearch,
        productAnalysis,
        rankHistoryWatch,
        autoComplete,
        productGenerate,
        changeUrl,
    };

    const headerQuotaEntries = useMemo(() => {
        const canon = toCanonicalPath(location.pathname);
        return features.filter(
            f => f.headerVisible && f.clientPath && toCanonicalPath(f.clientPath) === canon
        );
    }, [location.pathname, features]);

    const handleLogout = () => {
        AuthService.logout({ redirect: false });
        navigate('/login');
    };

    // DB(menus) 기반 사용자 메뉴 구성:
    // - 로그인 응답에서 서버가 menus 테이블 기반으로 필터링한 naverMenu/toolMenu를 localStorage에 저장
    // - 여기서는 localStorage를 읽어 label/path/icon을 그대로 sidebar에 반영
    useEffect(() => {
        const parse = key => {
            try {
                const raw = localStorage.getItem(key);
                return raw ? JSON.parse(raw) : [];
            } catch (e) {
                return [];
            }
        };

        const naverMenuRaw = parse('tsad_ranking_naverMenu');
        const toolMenuRaw = parse('tsad_ranking_toolMenu');
        const guideMenuRaw = parse('tsad_ranking_guideMenu');

        // use_yn 없으면 표시(기존 DB 호환). 'N'이면 숨김.
        const useYnFilter = m => {
            const v = (m?.use_yn ?? 'Y').toString().trim().toUpperCase();
            return v === 'Y' || v === '1' || v === 'TRUE';
        };
        const naverMenu = (naverMenuRaw || []).filter(useYnFilter);
        const toolMenu = (toolMenuRaw || []).filter(useYnFilter);
        const guideMenu = (guideMenuRaw || []).filter(useYnFilter);

        const sortBySortOrder = (a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0);

        /** public/icons.min.css = Font Awesome 5 — fa-solid(FA6) 클래스는 미표시됨 */
        const MENU_ICON_FALLBACK_BY_PATH = {
            '/integratedSearch': 'fas fa-layer-group',
        };

        const resolveMenuIconClass = (rawIcon, path) => {
            const canon = toCanonicalPath(path);
            const fallback = MENU_ICON_FALLBACK_BY_PATH[canon];
            const cls = typeof rawIcon === 'string' ? rawIcon.trim() : '';
            if (!cls) return fallback || null;
            if (cls.startsWith('fa-solid ') || cls.includes('magnifying-glass-chart')) {
                return fallback || cls.replace(/^fa-solid\b/, 'fas');
            }
            return cls;
        };

        const renderIcon = iconClass => {
            if (!iconClass || typeof iconClass !== 'string') return null;
            return (
                <i className={iconClass} style={{ fontSize: 16, width: 16, lineHeight: '16px' }} />
            );
        };

        const all = [...(naverMenu || []), ...(toolMenu || []), ...(guideMenu || [])];
        const labelMap = {};
        all.filter(m => m?.path && typeof m.path === 'string').forEach(m => {
            const label = m?.label ?? m.path;
            const canonical = toCanonicalPath(m.path);
            labelMap[canonical] = label;
            labelMap[canonical.toLowerCase()] = label;
        });
        setMenuLabelByPath(labelMap);

        const buildGroup = (menusArr, groupKey, groupLabel) => {
            if (!menusArr || menusArr.length === 0) return null;
            const isMenuCollapsed = collapsed && !isMobile;
            return {
                key: groupKey,
                label: groupLabel,
                type: 'group',
                children: [...menusArr].sort(sortBySortOrder).map(m => {
                    const isNew =
                        m.is_new === 'new' ||
                        Number(m.is_new) === 1 ||
                        String(m.is_new).trim() === '1';
                    const iconClass = resolveMenuIconClass(m.icon, m.path);
                    return {
                        key: toCanonicalPath(m.path),
                        label:
                            isNew && !isMenuCollapsed ? (
                                <span
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                >
                                    {m.label}
                                    <span
                                        className="menu-new-badge"
                                        style={{
                                            display: 'inline-block',
                                            background: '#ff4d4f',
                                            color: '#fff',
                                            fontSize: 10,
                                            fontWeight: 700,
                                            padding: '1px 5px',
                                            borderRadius: 4,
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        N
                                    </span>
                                </span>
                            ) : (
                                m.label
                            ),
                        icon:
                            isNew && isMenuCollapsed ? (
                                <span
                                    style={{
                                        position: 'relative',
                                        display: 'inline-block',
                                        overflow: 'visible',
                                    }}
                                >
                                    <i
                                        className={iconClass}
                                        style={{ fontSize: 16, width: 16, lineHeight: '16px' }}
                                    />
                                    <span
                                        style={{
                                            position: 'absolute',
                                            top: 5,
                                            left: 20,
                                            background: '#ff4d4f',
                                            color: '#fff',
                                            fontSize: 10,
                                            fontWeight: 700,
                                            padding: '2px 4px',
                                            borderRadius: 4,
                                            lineHeight: 1,
                                            pointerEvents: 'none',
                                            zIndex: 10,
                                        }}
                                    >
                                        N
                                    </span>
                                </span>
                            ) : (
                                renderIcon(iconClass)
                            ),
                    };
                }),
            };
        };

        const built = [
            buildGroup(naverMenu, 'naver', '네이버'),
            buildGroup(toolMenu, 'tools', '도구'),
            buildGroup(guideMenu, 'guide', '이용 안내'),
        ].filter(Boolean);

        setMenuItems(built);
    }, [collapsed, isMobile]);

    const onClickMenu = e => {
        const key = e?.key;
        if (typeof key !== 'string') {
            navigate(key);
            return;
        }

        navigate(toCanonicalPath(key));
    };

    const getPageTitle = () => {
        const pathMapping = {
            '/findNaverRank': '네이버 순위 조회',
            '/findNaverRankTracking': '네이버 순위 추적',
            '/keywordVer1': '키워드 추출순위 Ver1',
            '/keywordVer2': '키워드 추출순위 Ver2',
            '/NaverIntegratedRank': '키워드 통검 순위',
            '/KeywordRankVolumeCheck': '통합작업키워드추출',
            '/findMid': 'MID 조회',
            '/findProductByMid': '상품정보 조회',
            '/changeUrl': '상품 주소 변환',
            '/countText': '글자수 세기',
            '/integratedSearch': '통합 키워드 검색',
        };
        const currentPath = location.pathname || '';
        const lowerPath = currentPath?.toLowerCase?.();
        return (
            menuLabelByPath[currentPath] ||
            menuLabelByPath[lowerPath] ||
            pathMapping[currentPath] ||
            'ADCARE'
        );
    };

    // Tooltip/Popover contents mapping
    const getGuideContent = () => {
        const path = location.pathname;
        if (path === '/findNaverRank') {
            return (
                <div style={{ maxWidth: '350px', fontSize: '13px', lineHeight: '1.8' }}>
                    <Text
                        strong
                        style={{ display: 'block', marginBottom: '12px', fontSize: '15px' }}
                    >
                        네이버 순위 조회 안내
                    </Text>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        <li>
                            키워드와 단일 MID를 입력한 후, 조회 버튼을 누르면 네이버 순위를 확인할
                            수 있습니다.
                        </li>
                        <li>네이버 상품만 조회 가능합니다.</li>
                    </ul>
                </div>
            );
        }
        if (path === '/findNaverRankTracking') {
            return (
                <div style={{ maxWidth: '350px' }}>
                    <Text
                        strong
                        style={{ display: 'block', marginBottom: '12px', fontSize: '15px' }}
                    >
                        순위 추적 안내
                    </Text>
                    <ul
                        style={{
                            margin: 0,
                            paddingLeft: '20px',
                            fontSize: '13px',
                            lineHeight: '1.8',
                        }}
                    >
                        <li>네이버 상품만 조회 가능합니다. (등급에 따라 사용 가능 행 수 상이)</li>
                        <li>
                            <Text type="danger">
                                순위 조회를 위해 키워드와 단일 MID, 원부 MID만 입력하시면 됩니다.
                            </Text>
                        </li>
                        <li>매일 오후 15:30 자동 실행 됩니다.</li>
                    </ul>
                </div>
            );
        }
        if (path === '/keywordVer1') {
            return (
                <div style={{ maxWidth: '350px' }}>
                    <Text
                        strong
                        style={{ display: 'block', marginBottom: '12px', fontSize: '15px' }}
                    >
                        키워드 분석 안내
                    </Text>
                    <div style={{ fontSize: '13px', lineHeight: '1.8' }}>
                        ▪️ 정보를 입력 후 조회 버튼을 클릭하세요.
                        <br />
                        ▪️ <Text type="danger">1000위까지만 조회됩니다.</Text>
                    </div>
                </div>
            );
        }
        if (path === '/findMid') {
            return (
                <div style={{ maxWidth: '380px', fontSize: '13px', lineHeight: '1.8' }}>
                    <Text
                        strong
                        style={{ display: 'block', marginBottom: '12px', fontSize: '15px' }}
                    >
                        MID 조회 안내
                    </Text>
                    <div style={{ marginBottom: '8px' }}>
                        키워드와 상품 페이지 URL을 모두 입력하거나 붙여넣으세요.
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            입력 예시: [키워드] 등산스틱 / [URL]{' '}
                            https://smartstore.naver.com/adhelp/products/12345678901
                        </Text>
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        <li>URL이 빠진 행은 조회되지 않습니다.</li>
                        <li>자사몰, 오픈마켓 상품은 조회되지 않습니다.</li>
                        <li>입력값이 잘못된 경우, 상품 정보가 표시되지 않습니다.</li>
                    </ul>
                </div>
            );
        }
        if (path === '/findProductByMid') {
            return (
                <div style={{ maxWidth: '380px', fontSize: '13px', lineHeight: '1.8' }}>
                    <Text
                        strong
                        style={{ display: 'block', marginBottom: '12px', fontSize: '15px' }}
                    >
                        상품정보 조회 안내
                    </Text>
                    <div style={{ marginBottom: '8px' }}>
                        키워드와 상품번호 또는 MID를 모두 입력하세요.
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            입력 예시: [키워드] 등산스틱 / [상품번호] 12345678901
                        </Text>
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        <li>키워드 또는 상품번호가 빠진 행은 조회되지 않습니다.</li>
                        <li>원부 상품, 자사몰, 오픈마켓 상품은 조회되지 않습니다.</li>
                        <li>최대 1,000등까지 순위 조회가 가능합니다.</li>
                        <li>성인인증이 필요한 상품은 조회되지 않습니다.</li>
                    </ul>
                </div>
            );
        }
        if (path === '/countText') {
            return (
                <div style={{ maxWidth: '380px', fontSize: '13px', lineHeight: '1.8' }}>
                    <Text
                        strong
                        style={{ display: 'block', marginBottom: '12px', fontSize: '15px' }}
                    >
                        글자수 세기 안내
                    </Text>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        <li>
                            텍스트나 상품명을 입력하면 글자 수·단어 수를 실시간으로 확인할 수
                            있습니다.
                        </li>
                        <li>
                            공백으로 구분된 단어를 클릭해 키워드로 선택할 수 있습니다. (최대 5개)
                        </li>
                        <li>
                            선택한 키워드로 검색량 조회를 실행하면 연관 키워드 월간 검색량을 볼 수
                            있습니다.
                        </li>
                    </ul>
                </div>
            );
        }
        if (path === '/NaverIntegratedRank') {
            return (
                <div style={{ maxWidth: '380px', fontSize: '13px', lineHeight: '1.8' }}>
                    <Text
                        strong
                        style={{ display: 'block', marginBottom: '12px', fontSize: '15px' }}
                    >
                        키워드 통합검색 순위 안내
                    </Text>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        <li>키워드와 상품번호(MID)를 입력하거나 붙여넣기 후 조회하세요.</li>
                        <li>통합검색 광고·일반 순위를 확인할 수 있습니다.</li>
                    </ul>
                </div>
            );
        }
        if (path === '/KeywordRankVolumeCheck') {
            return (
                <div style={{ maxWidth: '380px', fontSize: '13px', lineHeight: '1.8' }}>
                    <Text
                        strong
                        style={{ display: 'block', marginBottom: '12px', fontSize: '15px' }}
                    >
                        통합작업키워드추출 안내
                    </Text>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        <li>키워드와 판매처URL을 입력하세요.</li>
                        <li>대표 키워드 1,000위 이내 상품만 조회됩니다.</li>
                        <li>원부URL이 있으면 원부URL 기준으로 데이터가 추출됩니다.</li>
                    </ul>
                </div>
            );
        }
        if (path === '/changeUrl') {
            return (
                <div style={{ maxWidth: '380px', fontSize: '13px', lineHeight: '1.8' }}>
                    <Text
                        strong
                        style={{ display: 'block', marginBottom: '12px', fontSize: '15px' }}
                    >
                        상품 주소 변환 안내
                    </Text>
                    <div style={{ marginBottom: '8px' }}>
                        상품 페이지 URL에서 불필요한 쿼리 파라미터를 제거합니다.
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        <li>
                            예시 1) ...products/12345<Text type="danger">?q=abc</Text> → 쿼리 제거
                        </li>
                        <li>
                            예시 2) ...goods/12345<Text type="danger">?defc=789</Text> → 쿼리 제거
                        </li>
                        <li>한 줄에 하나씩 URL을 입력하세요.</li>
                        <li>유효하지 않은 URL은 ❌ 표시됩니다.</li>
                    </ul>
                </div>
            );
        }
        if (path === '/rankHistoryWatch') {
            return (
                <div style={{ maxWidth: '380px', fontSize: '13px', lineHeight: '1.8' }}>
                    <Text
                        strong
                        style={{ display: 'block', marginBottom: '12px', fontSize: '15px' }}
                    >
                        과거 순위 조회 안내
                    </Text>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        <li>
                            네이버 쇼핑 상품 URL과 대표/메인 키워드를 정확히 입력하셔야 등록 및
                            확인이 가능합니다. (맞춤법/오타 주의)
                        </li>
                        <li>
                            네이버 쇼핑 기준 <Text strong>100위 이내</Text> 상품만 순위 조회가
                            가능합니다.
                        </li>
                        <li>
                            <Text type="danger">
                                ※ 조회 실패 및 데이터 미수집 상태(신규 등록 등)에서는 새로고침 시
                                한도가 차감되지 않습니다.
                            </Text>
                        </li>
                    </ul>
                </div>
            );
        }
        if (path === '/autoComplete') {
            return (
                <div style={{ maxWidth: '380px', fontSize: '13px', lineHeight: '1.8' }}>
                    <Text
                        strong
                        style={{ display: 'block', marginBottom: '12px', fontSize: '15px' }}
                    >
                        N자완추출 안내
                    </Text>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        <li>
                            추출검색어는 네이버 자동완성 <Text strong>순서대로</Text> 저장됩니다.
                        </li>
                        <li>
                            재검색은 <Text strong>10분에 한 번</Text>만 가능합니다.
                        </li>
                        <li>키워드 저장 수는 관리자가 설정한 한도까지 등록 가능합니다.</li>
                    </ul>
                </div>
            );
        }
        return null;
    };

    return (
        <Layout style={{ height: '100vh' }}>
            <div
                className="main-layout-sider-rail"
                style={{
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    zIndex: 101,
                    display: isMobile ? 'none' : 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: 0,
                }}
            >
                <Sider
                    trigger={null}
                    collapsible
                    collapsed={collapsed}
                    breakpoint="lg"
                    onBreakpoint={broken => {
                        if (broken) setCollapsed(true);
                    }}
                    width={220}
                    style={{
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        height: '100vh',
                        background: '#001529',
                        boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
                    }}
                >
                    <div
                        role="button"
                        tabIndex={0}
                        style={{
                            height: 44,
                            margin: '12px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                        }}
                        onClick={() => navigate('/dashboard')}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') navigate('/dashboard');
                        }}
                    >
                        <h2
                            style={{
                                color: '#fff',
                                margin: 0,
                                fontSize: collapsed ? '1rem' : '1.4rem',
                                fontWeight: 800,
                                letterSpacing: '1px',
                            }}
                        >
                            {collapsed ? 'AC' : 'ADCARE'}
                        </h2>
                    </div>
                    <Menu
                        theme="dark"
                        mode="inline"
                        defaultSelectedKeys={[location.pathname]}
                        selectedKeys={[location.pathname]}
                        items={menuItems}
                        onClick={onClickMenu}
                        style={{ borderRight: 0 }}
                    />
                </Sider>
                {/* 접기 버튼 — Sider 다음 플렉스 자식으로 두어 너비 애니메이션과 동기화(틈 방지) */}
                <div
                    className="main-layout-sider-collapse-tab"
                    role="button"
                    tabIndex={0}
                    onClick={() => setCollapsed(!collapsed)}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') setCollapsed(!collapsed);
                    }}
                    aria-label={collapsed ? 'expand menu' : 'collapse menu'}
                    style={{
                        flexShrink: 0,
                        marginTop: 12,
                        zIndex: 2,
                    }}
                >
                    {collapsed ? (
                        <RightOutlined className="main-layout-sider-collapse-tab-icon" />
                    ) : (
                        <LeftOutlined className="main-layout-sider-collapse-tab-icon" />
                    )}
                </div>
            </div>
            <Layout
                className="main-content-layout"
                style={{
                    marginLeft: isMobile ? 0 : (collapsed ? 80 : 220),
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100vh',
                    ...(location.pathname === '/countText'
                        ? { overflow: 'hidden' }
                        : {}),
                }}
            >
                <Header
                    style={{
                        padding: '0 24px 0 28px',
                        background: '#f4f6f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        position: 'sticky',
                        top: 0,
                        zIndex: 100,
                        width: '100%',
                        height: '48px',
                        borderBottom: '1px solid #e4e7ed',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                        flexShrink: 0,
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {isMobile && (
                            <MenuOutlined
                                onClick={() => setMobileDrawerOpen(true)}
                                style={{ fontSize: '18px', cursor: 'pointer', marginRight: '4px' }}
                            />
                        )}
                        {/* 페이지 타이틀 */}
                        <Space align="center" size={8}>
                            <span
                                className="main-layout-page-title"
                                style={{
                                    fontSize: '15px',
                                    fontWeight: 700,
                                    color: '#1e293b',
                                    margin: 0,
                                    lineHeight: 1,
                                    letterSpacing: '-0.01em',
                                }}
                            >
                                {getPageTitle()}
                            </span>
                            {toCanonicalPath(location.pathname) === '/integratedSearch' && (
                                <Button
                                    size="small"
                                    onClick={() =>
                                        window.dispatchEvent(
                                            new CustomEvent('tsad-open-integrated-search-guide')
                                        )
                                    }
                                    style={{ fontSize: 11, height: 22, padding: '0 8px' }}
                                >
                                    가이드
                                </Button>
                            )}
                            {getGuideContent() && (
                                <Popover
                                    content={getGuideContent()}
                                    trigger="hover"
                                    placement="bottomLeft"
                                    mouseEnterDelay={0.15}
                                    mouseLeaveDelay={0.1}
                                >
                                    <QuestionCircleOutlined
                                        className="guide-help-icon"
                                        style={{
                                            color: '#94a3b8',
                                            cursor: 'pointer',
                                            fontSize: '15px',
                                        }}
                                    />
                                </Popover>
                            )}
                        </Space>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            textAlign: 'center',
                            height: '48px',
                        }}
                    >
                        {headerQuotaEntries.map(({ queryType, badgeLabel }) => {
                            const q = quotaByKey[queryType];
                            if (!q) return null;
                            return (
                                <QuotaBadge
                                    key={queryType}
                                    quota={q.quota}
                                    pct={q.pct}
                                    label={badgeLabel}
                                />
                            );
                        })}
                        <Popover
                            trigger="click"
                            placement="bottomRight"
                            overlayInnerStyle={{ padding: 0 }}
                            content={(() => {
                                const orderedPaths = menuItems.flatMap(g =>
                                    (g?.children || []).map(c => c.key)
                                );
                                const seen = new Set();
                                const entries = orderedPaths
                                    .flatMap(path =>
                                        features.filter(
                                            f =>
                                                f.clientPath &&
                                                toCanonicalPath(f.clientPath) === path
                                        )
                                    )
                                    .filter(f => {
                                        if (seen.has(f.queryType)) return false;
                                        seen.add(f.queryType);
                                        return !!allQuotas[f.queryType];
                                    });
                                const pathCount = {};
                                entries.forEach(f => {
                                    const p = toCanonicalPath(f.clientPath);
                                    pathCount[p] = (pathCount[p] || 0) + 1;
                                });
                                return (
                                    <div style={{ width: 280 }}>
                                        {/* 프로필 - 고정 영역 */}
                                        <div
                                            style={{
                                                padding: '12px 16px 12px',
                                                borderBottom: '1px solid #f0f0f0',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                            }}
                                        >
                                            <div>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                    }}
                                                >
                                                    <Text strong style={{ fontSize: 14 }}>
                                                        {uname}
                                                    </Text>
                                                    {currentUser?.kakao_id && (
                                                        <span
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 3,
                                                                background: '#FEE500',
                                                                color: '#3C1E1E',
                                                                fontSize: 10,
                                                                fontWeight: 600,
                                                                borderRadius: 4,
                                                                padding: '1px 5px',
                                                                lineHeight: '16px',
                                                            }}
                                                        >
                                                            카카오
                                                        </span>
                                                    )}
                                                </div>
                                                {grade.toLowerCase() === 'premium' ? (
                                                    <div
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 4,
                                                            marginTop: 4,
                                                            background:
                                                                'linear-gradient(90deg, #1677ff, #722ed1)',
                                                            color: '#fff',
                                                            padding: '2px 8px',
                                                            borderRadius: 12,
                                                            fontSize: 11,
                                                            fontWeight: 800,
                                                        }}
                                                    >
                                                        <RocketFilled
                                                            style={{ color: '#ffec3d' }}
                                                        />
                                                        <span>PREMIUM 사용자</span>
                                                    </div>
                                                ) : grade.toLowerCase() === 'basic' ? (
                                                    <div
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 4,
                                                            marginTop: 4,
                                                            background:
                                                                'linear-gradient(90deg, #13c2c2, #08979c)',
                                                            color: '#fff',
                                                            padding: '2px 8px',
                                                            borderRadius: 12,
                                                            fontSize: 11,
                                                            fontWeight: 800,
                                                        }}
                                                    >
                                                        <StarFilled style={{ color: '#ffec3d' }} />
                                                        <span>BASIC 사용자</span>
                                                    </div>
                                                ) : grade.toLowerCase() === 'vip' ? (
                                                    <div
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 4,
                                                            marginTop: 4,
                                                            background:
                                                                'linear-gradient(90deg, #d48806, #ad6800)',
                                                            color: '#fff',
                                                            padding: '2px 8px',
                                                            borderRadius: 12,
                                                            fontSize: 11,
                                                            fontWeight: 800,
                                                        }}
                                                    >
                                                        <CrownFilled style={{ color: '#fff' }} />
                                                        <span>VIP 사용자</span>
                                                    </div>
                                                ) : (
                                                    <div style={{ marginTop: 4 }}>
                                                        <Text
                                                            type="secondary"
                                                            style={{
                                                                fontSize: 12,
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {grade.toUpperCase()}
                                                        </Text>
                                                        <Text
                                                            type="secondary"
                                                            style={{ fontSize: 11, marginLeft: 6 }}
                                                        >
                                                            무료 사용자
                                                        </Text>
                                                    </div>
                                                )}
                                                {currentUser?.grade &&
                                                    currentUser.grade !== 'user' &&
                                                    subscriptionEndDate &&
                                                    (() => {
                                                        const end = new Date(subscriptionEndDate);
                                                        const formattedEnd = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
                                                        const today = new Date();
                                                        today.setHours(0, 0, 0, 0);
                                                        end.setHours(0, 0, 0, 0);
                                                        const diff = Math.ceil(
                                                            (end - today) / 86400000
                                                        );
                                                        const dLabel =
                                                            diff > 0
                                                                ? `D-${diff}`
                                                                : diff === 0
                                                                  ? 'D-Day'
                                                                  : `D+${Math.abs(diff)}`;
                                                        const badgeColor =
                                                            diff <= 0
                                                                ? '#fff1f0'
                                                                : diff <= 7
                                                                  ? '#fff7e6'
                                                                  : '#e6f4ff';
                                                        const textColor =
                                                            diff <= 0
                                                                ? '#cf1322'
                                                                : diff <= 7
                                                                  ? '#fa8c16'
                                                                  : '#1677ff';
                                                        return (
                                                            <div
                                                                style={{
                                                                    marginTop: 6,
                                                                    fontSize: 12,
                                                                    color: '#595959',
                                                                    whiteSpace: 'nowrap',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                }}
                                                            >
                                                                <span>
                                                                    결제/만료일:{' '}
                                                                    <strong
                                                                        style={{ color: '#262626' }}
                                                                    >
                                                                        {formattedEnd}
                                                                    </strong>
                                                                </span>
                                                                <span
                                                                    style={{
                                                                        marginLeft: 6,
                                                                        background: badgeColor,
                                                                        color: textColor,
                                                                        padding: '2px 6px',
                                                                        borderRadius: 4,
                                                                        fontWeight: 700,
                                                                        fontSize: 11,
                                                                    }}
                                                                >
                                                                    {dLabel}
                                                                </span>
                                                            </div>
                                                        );
                                                    })()}
                                            </div>
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                onClick={handleLogout}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter' || e.key === ' ')
                                                        handleLogout();
                                                }}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 5,
                                                    cursor: 'pointer',
                                                    color: '#ff4d4f',
                                                    fontSize: 12,
                                                    padding: '2px 0',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <LogoutOutlined />
                                                <span>로그아웃</span>
                                            </div>
                                        </div>
                                        {/* 한도 현황 - 스크롤 영역 */}
                                        {entries.length > 0 && (
                                            <div
                                                style={{
                                                    maxHeight: 'calc(100vh - 200px)',
                                                    overflowY: 'auto',
                                                    padding: '12px 16px 10px',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                        marginBottom: 8,
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            flex: 1,
                                                            height: 1,
                                                            background: '#e8e8e8',
                                                        }}
                                                    />
                                                    <Text
                                                        style={{
                                                            fontSize: 10,
                                                            fontWeight: 600,
                                                            color: '#aaa',
                                                            whiteSpace: 'nowrap',
                                                            letterSpacing: '0.04em',
                                                        }}
                                                    >
                                                        오늘 사용 현황
                                                    </Text>
                                                    <div
                                                        style={{
                                                            flex: 1,
                                                            height: 1,
                                                            background: '#e8e8e8',
                                                        }}
                                                    />
                                                </div>
                                                {entries.map(f => {
                                                    const q = allQuotas[f.queryType];
                                                    const unlimited =
                                                        q.unlimited || q.remaining === -1;
                                                    const {
                                                        used = 0,
                                                        limit = 0,
                                                        bonus = 0,
                                                        remaining,
                                                    } = q;
                                                    const total = limit + bonus;
                                                    const pct = unlimited
                                                        ? 0
                                                        : total > 0
                                                          ? Math.min(
                                                                100,
                                                                Math.round((used / total) * 100)
                                                            )
                                                          : remaining <= 0
                                                            ? 100
                                                            : 0;
                                                    const exhausted = !unlimited && remaining <= 0;
                                                    const color =
                                                        exhausted || pct >= 90
                                                            ? '#ff4d4f'
                                                            : pct >= 70
                                                              ? '#faad14'
                                                              : '#2e5bff';
                                                    const p = toCanonicalPath(f.clientPath);
                                                    const base =
                                                        menuLabelByPath[p] ||
                                                        menuLabelByPath[p.toLowerCase()] ||
                                                        f.label;
                                                    const displayLabel =
                                                        pathCount[p] > 1
                                                            ? `${base} (${f.badgeLabel})`
                                                            : base;
                                                    const rowMax = f.rowGubun
                                                        ? allRowLimits[f.rowGubun.toLowerCase()]
                                                        : null;
                                                    return (
                                                        <div
                                                            key={f.queryType}
                                                            style={{ marginBottom: 5 }}
                                                        >
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    marginBottom: 2,
                                                                    alignItems: 'center',
                                                                }}
                                                            >
                                                                <span
                                                                    style={{
                                                                        fontSize: 12,
                                                                        color: '#595959',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                        minWidth: 0,
                                                                        flex: 1,
                                                                    }}
                                                                >
                                                                    {displayLabel}
                                                                </span>
                                                                <div
                                                                    style={{
                                                                        flexShrink: 0,
                                                                        marginLeft: 6,
                                                                        textAlign: 'right',
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            fontSize: 12,
                                                                            color,
                                                                            fontWeight: exhausted
                                                                                ? 700
                                                                                : 400,
                                                                            display: 'block',
                                                                            lineHeight: '1.2',
                                                                        }}
                                                                    >
                                                                        {unlimited
                                                                            ? '무제한'
                                                                            : exhausted
                                                                              ? '소진'
                                                                              : `${used}/${total}건`}
                                                                    </span>
                                                                    {rowMax != null && (
                                                                        <span
                                                                            style={{
                                                                                fontSize: 10,
                                                                                color: '#bbb',
                                                                                display: 'block',
                                                                                lineHeight: '1.2',
                                                                            }}
                                                                        >
                                                                            최대 {rowMax}행
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <Progress
                                                                percent={pct}
                                                                showInfo={false}
                                                                size={['100%', 7]}
                                                                strokeColor={color}
                                                                trailColor="#f0f0f0"
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        >
                            <div
                                className="user-profile-trigger"
                                style={{
                                    cursor: 'pointer',
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                <Avatar
                                    size={34}
                                    src={currentUser?.profile_image || undefined}
                                    icon={!currentUser?.profile_image && <UserOutlined />}
                                    style={{
                                        backgroundColor: '#e2e8f0',
                                        color: '#475569',
                                        border: '1.5px solid #cbd5e1',
                                        objectFit: 'cover',
                                    }}
                                />
                            </div>
                        </Popover>
                    </div>
                </Header>
                <Content
                    style={{
                        margin: 0,
                        padding: 0,
                        flex: 1,
                        minHeight: 0,
                        background: '#ffffff',
                        overflowY: 'auto',
                        paddingBottom: isMobile ? 0 : 32,
                        ...(location.pathname === '/countText'
                            ? {
                                  overflow: 'hidden',
                                  paddingBottom: 0,
                              }
                            : {}),
                    }}
                >
                    <div
                        style={{
                            flex: '1 0 auto',
                            ...(location.pathname === '/countText' ? { display: 'flex', flexDirection: 'column' } : {})
                        }}
                    >
                        <Outlet />
                    </div>
                    {isMobile && (
                        <footer
                            style={{
                                flexShrink: 0,
                                background: '#f8fafc',
                                borderTop: '1px solid #eef0f3',
                                userSelect: 'none',
                                padding: '24px 16px 80px',
                                textAlign: 'center',
                                width: '100%',
                            }}
                        >
                            <div style={{ fontSize: 13, fontWeight: 800, color: '#334155', marginBottom: 12 }}>
                                티에스애드(TsAd)
                            </div>
                            <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.8, marginBottom: 16 }}>
                                <div>대표: 김택수 | 사업자등록번호: 249-11-02555</div>
                                <div>주소: 경기도 용인시 수지구 포은대로59번길 37 6층 605호</div>
                                <div>이메일: tnxorrla123@naver.com | 전화: 010-5831-1070</div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
                                {[
                                    { label: '이용약관', key: 'terms' },
                                    { label: '개인정보처리방침', key: 'privacy' },
                                    { label: '결제가이드', key: 'payment' },
                                ].map(({ label, key }) => (
                                    <span
                                        key={key}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setPolicyModal(key)}
                                        onKeyDown={e => { if (e.key === 'Enter') setPolicyModal(key); }}
                                        style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', cursor: 'pointer' }}
                                    >
                                        {label}
                                    </span>
                                ))}
                            </div>
                        </footer>
                    )}
                </Content>
            </Layout>
            {/* PC 푸터: position fixed로 항상 하단 고정 */}
            {!isMobile && (
                <footer
                    style={{
                        position: 'fixed',
                        bottom: 0,
                        left: collapsed ? 80 : 220,
                        right: 0,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 32px',
                        background: '#f8fafc',
                        borderTop: '1px solid #eef0f3',
                        gap: 0,
                        userSelect: 'none',
                        overflow: 'hidden',
                        zIndex: 99,
                        transition: 'left 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                >
                    <span style={{ fontSize: 10, color: '#94a3b8', marginRight: 4 }}>상호</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', marginRight: 14 }}>티에스애드(TsAd)</span>
                    <Divider /><Item label="대표" value="김택수" />
                    <Divider /><Item label="사업자등록번호" value="249-11-02555" />
                    <Divider /><Item label="주소" value="경기도 용인시 수지구 포은대로59번길 37 6층 605호" />
                    <Divider /><Item label="이메일" value="tnxorrla123@naver.com" />
                    <Divider /><Item label="전화" value="010-5831-1070" />
                    <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                        {[
                            { label: '이용약관', key: 'terms' },
                            { label: '개인정보처리방침', key: 'privacy' },
                            { label: '결제가이드', key: 'payment' },
                        ].map(({ label, key }) => (
                            <span
                                key={key}
                                role="button"
                                tabIndex={0}
                                onClick={() => setPolicyModal(key)}
                                onKeyDown={e => { if (e.key === 'Enter') setPolicyModal(key); }}
                                style={{ fontSize: 10, color: '#94a3b8', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                onMouseEnter={e => e.target.style.color = '#475569'}
                                onMouseLeave={e => e.target.style.color = '#94a3b8'}
                            >
                                {label}
                            </span>
                        ))}
                    </span>
                </footer>
            )}
            <Modal
                open={domainModal}
                onCancel={() => setDomainModal(false)}
                footer={[
                    <Button key="go" type="primary" danger onClick={() => { window.location.href = 'https://adcare.kr'; }}>
                        adcare.kr 바로가기
                    </Button>,
                    <Button key="close" onClick={() => setDomainModal(false)}>닫기</Button>,
                ]}
                title="🔔 서비스 도메인이 변경되었습니다"
                width={420}
                centered
            >
                <div style={{ padding: '8px 0', lineHeight: 1.8 }}>
                    <p style={{ marginBottom: 12 }}>
                        ADHELP 서비스가 <b>ADCARE</b>로 새롭게 시작합니다.
                    </p>
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>기존 주소</div>
                        <div style={{ fontSize: 14, textDecoration: 'line-through', color: '#94a3b8' }}>adhelp.store</div>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8, marginBottom: 4 }}>새 주소</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#2563eb' }}>adcare.kr</div>
                    </div>
                    <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>
                        북마크를 새 주소로 변경해 주시면 계속 이용하실 수 있습니다.
                    </p>
                </div>
            </Modal>
            <Modal
                open={!!policyModal}
                onCancel={() => setPolicyModal(null)}
                footer={null}
                width={680}
                centered
                title={
                    policyModal === 'terms' ? '이용약관' :
                    policyModal === 'privacy' ? '개인정보처리방침' :
                    '결제가이드'
                }
                styles={{ body: { maxHeight: '65vh', overflowY: 'auto', padding: '20px 24px', fontSize: 13, lineHeight: 1.8, color: '#334155' } }}
            >
                {policyModal === 'terms' && (
                    <div>
                        <PolicySection title="제1조 (목적)">
                            {'본 약관은 티에스애드(TsAd)(이하 "회사")가 제공하는 ADCARE 서비스(이하 "서비스")의 이용과 관련하여 회사와 이용자 간의 권리, 의무 및 책임사항을 규정함을 목적으로 합니다.'}
                        </PolicySection>
                        <PolicySection title="제2조 (용어 정의)">
                            {'① "서비스"란 회사가 제공하는 네이버 쇼핑 순위 조회, 키워드 분석, 상품 정보 수집 등 일체의 기능을 말합니다.'}<br/>
                            {'② "이용자"란 본 약관에 동의하고 서비스를 이용하는 자를 말합니다.'}<br/>
                            {'③ "쿼터"란 등급별로 부여되는 일일 서비스 이용 한도를 말합니다.'}
                        </PolicySection>
                        <PolicySection title="제3조 (약관의 게시와 개정)">
                            ① 본 약관은 서비스 화면에 게시함으로써 효력이 발생합니다.<br/>
                            ② 회사는 약관을 개정할 경우 <b>적용일 30일 전(이용자에게 불리한 경우)</b> 또는 7일 전(그 외)에 공지합니다.<br/>
                            ③ 변경된 약관에 동의하지 않을 경우 서비스 이용을 중단하고 탈퇴할 수 있습니다.
                        </PolicySection>
                        <PolicySection title="제4조 (이용신청 및 회원가입)">
                            ① 회원가입은 만 14세 이상의 개인 또는 사업자에 한하여 가능합니다.<br/>
                            ② 회원은 실명으로 가입하여야 하며, 타인 정보 도용 시 이용이 제한됩니다.<br/>
                            ③ 다음의 경우 가입을 거부할 수 있습니다.<br/>
                            &nbsp;&nbsp;· 허위 정보 등록 / 이전 회원자격 상실 이력 / 기타 기술적으로 지장이 있는 경우
                        </PolicySection>
                        <PolicySection title="제5조 (이용계약의 종료)">
                            <b>회원의 해지:</b> 회원은 언제든지 서비스 내 탈퇴 기능을 통해 해지할 수 있으며, 해지 즉시 모든 데이터가 삭제됩니다.<br/>
                            <b>회사의 해지:</b> 허위 정보 등록, 타인 정보 도용, 서비스 방해, 약관 위반 시 이용계약을 해지할 수 있습니다.
                        </PolicySection>
                        <PolicySection title="제6조 (서비스 제공 및 중단)">
                            ① 회사는 네이버 쇼핑 순위 조회, 키워드 분석, 상품 정보 수집 등의 서비스를 제공합니다.<br/>
                            ② 시스템 점검, 장애, 천재지변 등으로 서비스를 일시 중단할 수 있습니다.<br/>
                            ③ 회사의 고의·과실 없이 발생한 중단에 대해서는 배상 책임을 지지 않습니다.
                        </PolicySection>
                        <PolicySection title="제7조 (구독 서비스 및 플랜 변경)">
                            ① <b>한달 이용권:</b> 30일 단기 이용권 (만료 후 자동 종료)<br/>
                            ② <b>구독 자동연장:</b> 매월 자동 결제. 원하지 않을 경우 직접 해지 필요<br/>
                            ③ 구독 취소 후에도 <b>잔여 기간까지 서비스 이용 가능</b><br/>
                            ④ <b>등급 업그레이드:</b> 현재 구독 해지 후 원하는 등급으로 새로 결제 (기존 만료일까지 현재 등급 이용 가능)<br/>
                            ⑤ <b>부가세(VAT):</b> 표시 금액은 공급가액(VAT 별도)이며, 실 청구 금액에는 VAT 10%가 포함됩니다.
                        </PolicySection>
                        <PolicySection title="제8조 (청약철회 및 환불)">
                            ① 전자상거래법 제17조에 따라 결제일로부터 <b>7일 이내</b> 청약철회 요청 가능합니다.<br/>
                            ② 서비스 이용(쿼터 사용) 개시 시 사용 기간에 대해 일할 계산하여 환불됩니다.<br/>
                            ③ 환불금액: (결제금액 ÷ 30일) × 잔여 일수, 100원 미만 절사<br/>
                            ④ 환불은 승인 후 영업일 기준 3~5일 이내 처리됩니다.<br/>
                            ⑤ 약관 위반 또는 중복 환불 요청은 환불 불가합니다.<br/>
                            ⑥ 환불 문의: 고객센터 010-5831-1070 · tnxorrla123@naver.com
                        </PolicySection>
                        <PolicySection title="제9조 (회사의 의무)">
                            ① 관련 법령을 준수하고 지속적·안정적으로 서비스를 제공하기 위해 최선을 다합니다.<br/>
                            ② 이용자의 개인정보 보호를 위한 보안 시스템을 갖추고 운영합니다.<br/>
                            ③ 이용자가 원하지 않는 영리 목적의 광고성 전자우편을 발송하지 않습니다.
                        </PolicySection>
                        <PolicySection title="제10조 (회원의 의무 및 금지 행위)">
                            ① 허위 정보 등록 또는 타인 정보 도용<br/>
                            ② 서비스를 통해 수집한 데이터의 무단 재배포 또는 상업적 이용<br/>
                            ③ 자동화 도구(크롤링 등)를 이용한 비정상적 서비스 접근<br/>
                            ④ 타인의 계정 도용 또는 공유<br/>
                            ⑤ 회사 및 제3자의 저작권 등 지적재산권 침해
                        </PolicySection>
                        <PolicySection title="제11조 (저작권 귀속)">
                            ① 회사가 작성한 저작물에 대한 저작권 및 지적재산권은 회사에 귀속합니다.<br/>
                            ② 이용자는 서비스를 통해 얻은 정보를 회사의 사전 승낙 없이 복제·배포·영리 목적으로 이용할 수 없습니다.
                        </PolicySection>
                        <PolicySection title="제12조 (분쟁 해결)">
                            ① 회사는 이용자의 정당한 불만 및 피해 보상을 위해 고객센터를 운영합니다.<br/>
                            ② 본 약관 관련 분쟁은 대한민국 법률을 준거법으로 하며, 회사 소재지 관할 법원을 제1심 법원으로 합니다.
                        </PolicySection>
                        <p style={{ marginTop: 20, fontSize: 12, color: '#94a3b8' }}>시행일: 2026년 6월 1일</p>
                    </div>
                )}
                {policyModal === 'privacy' && (
                    <div>
                        <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.8, marginBottom: 14 }}>
                            {'티에스애드(TsAd)(이하 "회사")는 개인정보보호법에 따라 이용자의 개인정보 보호 및 권익을 보호하고 개인정보 관련 고충을 원활하게 처리할 수 있도록 다음과 같은 처리방침을 두고 있습니다.'}
                        </p>
                        <PolicySection title="제1조 (개인정보의 처리 목적)">
                            <b>1. 회원가입 및 관리:</b> 회원 가입의사 확인, 본인 식별·인증, 회원자격 유지·관리, 서비스 부정이용 방지, 각종 고지·통지<br/>
                            <b>2. 서비스 제공:</b> 순위 조회, 키워드 분석 등 서비스 제공, 콘텐츠 제공, 맞춤서비스, 본인인증, 요금결제·정산<br/>
                            <b>3. 고객 문의 대응:</b> 불만처리, 공지사항 전달
                        </PolicySection>
                        <PolicySection title="제2조 (개인정보의 처리 및 보유 기간)">
                            법령에 따른 보유기간 또는 동의받은 기간 내에서 처리·보유합니다.<br/>
                            · 회원 정보: 회원 탈퇴 시까지<br/>
                            · 서비스 이용 기록: 3년<br/>
                            · 결제 정보: 5년 (전자상거래법)<br/>
                            · 고객 문의 기록: 3년<br/>
                            · 서비스 접속 로그: 3개월 (통신비밀보호법)
                        </PolicySection>
                        <PolicySection title="제3조 (개인정보의 제3자 제공)">
                            이용자의 개인정보를 원칙적으로 제3자에게 제공하지 않습니다. 단, 다음의 경우 예외로 합니다.<br/>
                            · 이용자가 사전에 동의한 경우<br/>
                            · 법령의 규정에 의거하거나 수사기관의 요구가 있는 경우
                        </PolicySection>
                        <PolicySection title="제4조 (개인정보 처리 위탁)">
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc' }}>
                                        <th style={{ padding: '7px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontWeight: 600 }}>위탁받는 자</th>
                                        <th style={{ padding: '7px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontWeight: 600 }}>위탁 업무</th>
                                        <th style={{ padding: '7px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontWeight: 600 }}>보유 기간</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr><td style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9', color: '#475569' }}>토스페이먼츠(주)</td><td style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9', color: '#475569' }}>결제 및 정산 업무</td><td style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9', color: '#475569' }}>계약 종료 시까지</td></tr>
                                    <tr><td style={{ padding: '7px 10px', color: '#475569' }}>카카오(주)</td><td style={{ padding: '7px 10px', color: '#475569' }}>소셜 로그인 인증</td><td style={{ padding: '7px 10px', color: '#475569' }}>회원 탈퇴 시까지</td></tr>
                                </tbody>
                            </table>
                        </PolicySection>
                        <PolicySection title="제5조 (처리하는 개인정보 항목)">
                            <b>회원가입 시 (필수):</b> 이름(닉네임), 이메일, 비밀번호(암호화), 휴대전화번호<br/>
                            <b>소셜 로그인 시 (선택):</b> 카카오 계정 정보<br/>
                            <b>서비스 이용 시 (자동수집):</b> 서비스 이용 기록, IP 주소, 접속 일시, 검색 키워드·이력, 일별 사용량<br/>
                            <b>결제 시:</b> 토스페이먼츠 빌링키, 결제금액·일시·주기, 구독 플랜 정보
                        </PolicySection>
                        <PolicySection title="제6조 (개인정보의 파기)">
                            보유기간 경과 또는 처리목적 달성 시 지체없이 파기합니다.<br/>
                            <b>파기 절차:</b> 개인정보 보호책임자 승인 후 파기<br/>
                            <b>파기 방법:</b> 전자적 파일은 복원 불가한 기술적 방법으로 삭제, 출력물은 분쇄·소각
                        </PolicySection>
                        <PolicySection title="제7조 (개인정보의 안전성 확보 조치)">
                            ① 개인정보 취급 직원 최소화 및 교육<br/>
                            ② 내부관리계획 수립·시행<br/>
                            ③ 비밀번호 암호화 저장, 중요 데이터 전송 구간 암호화<br/>
                            ④ 보안 프로그램 설치 및 주기적 점검, 외부 접근 차단<br/>
                            ⑤ 데이터베이스 접근권한 통제 및 침입차단시스템 운영
                        </PolicySection>
                        <PolicySection title="제8조 (쿠키 운영)">
                            회사는 로그인 상태 유지를 위한 <b>세션 쿠키만</b> 사용합니다. 로그아웃 시 자동 삭제되며, 광고·마케팅·분석용 추가 쿠키는 사용하지 않습니다.
                        </PolicySection>
                        <PolicySection title="제9조 (이용자의 권리)">
                            이용자는 언제든지 개인정보 열람, 정정, 삭제, 처리 정지를 요청할 수 있습니다.<br/>
                            문의: 고객센터 010-5831-1070 · tnxorrla123@naver.com
                        </PolicySection>
                        <PolicySection title="제10조 (개인정보 보호책임자)">
                            성명: 김택수 | 직책: 대표 | 연락처: 010-5831-1070 | 이메일: tnxorrla123@naver.com
                        </PolicySection>
                        <PolicySection title="제11조 (권익침해 구제방법)">
                            개인정보 침해 관련 구제를 받기 위해 아래 기관에 문의하실 수 있습니다.<br/>
                            · 개인정보분쟁조정위원회: 1833-6972 (www.kopico.go.kr)<br/>
                            · 개인정보침해신고센터: 118 (privacy.kisa.or.kr)<br/>
                            · 대검찰청: 1301 (www.spo.go.kr)<br/>
                            · 경찰청: 182 (ecrm.cyber.go.kr)
                        </PolicySection>
                        <PolicySection title="제12조 (처리방침 변경)">
                            이 방침은 시행일로부터 적용되며, 변경 시 시행 7일 전 공지사항을 통해 고지합니다.
                        </PolicySection>
                        <p style={{ marginTop: 20, fontSize: 12, color: '#94a3b8' }}>시행일: 2026년 6월 1일</p>
                    </div>
                )}
                {policyModal === 'payment' && (
                    <div>
                        <PolicySection title="제1조 (서비스 이용 안내)">
                            <b>FREE (무료):</b> 회원가입 시 기본 적용. 기본 쿼터 범위 내 이용 가능<br/>
                            <b>BASIC / PREMIUM (유료):</b> 확장·최대 쿼터 및 추가 기능 제공<br/><br/>
                            자세한 등급별 한도는{' '}
                            <span
                                role="button"
                                tabIndex={0}
                                onClick={() => { setPolicyModal(null); navigate('/grade-guide'); }}
                                onKeyDown={e => { if (e.key === 'Enter') { setPolicyModal(null); navigate('/grade-guide'); } }}
                                style={{ color: '#2e5bff', cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
                            >
                                서비스 요금 안내 페이지
                            </span>
                            에서 확인하세요.
                        </PolicySection>
                        <PolicySection title="제2조 (결제 안내)">
                            <b>결제 수단:</b> 신용카드, 체크카드 (토스페이먼츠 지원 수단)<br/>
                            <b>결제 방식:</b> 정기결제(빌링키) 방식으로 카드를 등록하여 자동 결제<br/><br/>
                            <b>결제 방법:</b><br/>
                            ① 서비스 화면 우측 상단 프로필 클릭<br/>
                            ② 등급 업그레이드 또는 구독 선택<br/>
                            ③ 결제 수단 입력 후 결제 완료 → 즉시 해당 등급 적용<br/><br/>
                            <b>부가세(VAT) 안내:</b> 서비스 요금 안내 페이지에 표시된 금액은 공급가액(VAT 별도)이며, 실제 청구 금액은 VAT 10%가 포함됩니다.<br/>
                            &nbsp;&nbsp;· BASIC: 공급가액 30,000원 → 실결제 33,000원 (VAT 3,000원 포함)<br/>
                            &nbsp;&nbsp;· PREMIUM: 공급가액 50,000원 → 실결제 55,000원 (VAT 5,000원 포함)
                        </PolicySection>
                        <PolicySection title="제3조 (이용권 종류)">
                            <b>한달 이용권:</b> 30일 단기 이용권. 만료 후 자동 종료 (재결제 필요)<br/>
                            <b>구독 한달 자동연장:</b> 매월 자동 결제. 구독 취소하지 않으면 30일마다 자동 갱신<br/>
                            · 자동 갱신은 언제든지 해지 가능<br/>
                            · 자동결제 등록 시 약관 동의 및 이메일 안내 발송
                        </PolicySection>
                        <PolicySection title="제4조 (등급 변경)">
                            <b>업그레이드 (예: BASIC → PREMIUM):</b><br/>
                            ① 프로필 {'>'} 구독 관리에서 현재 구독 해지<br/>
                            ② 해지 후에도 <b>기존 만료일까지 현재 등급 이용 가능</b><br/>
                            ③ 원하는 시점에 PREMIUM으로 새로 결제 → 즉시 적용<br/><br/>
                            <b>다운그레이드 (예: PREMIUM → BASIC):</b><br/>
                            · 현재 구독 해지 후 만료일 이후 하위 등급으로 재결제하시면 됩니다.
                        </PolicySection>
                        <PolicySection title="제5조 (구독 해지 및 이용 제한)">
                            ① 구독(자동연장) 이용 시 만료 전 직접 해지하지 않으면 자동 갱신됩니다.<br/>
                            ② 해지는 서비스 내 프로필 {'>'} 구독 관리에서 직접 처리하시기 바랍니다.<br/>
                            ③ <b>해지 후에도 잔여 기간 동안 서비스 이용이 가능합니다.</b><br/>
                            ④ 결제 실패 시 멤버십이 자동 해지되며 FREE 등급으로 전환됩니다.<br/>
                            ⑤ 일일 이용 한도 초과 시 다음 날 자정(KST)까지 해당 기능이 제한됩니다.<br/>
                            ⑥ 비정상적 대량 요청 또는 약관 위반 시 이용이 제한될 수 있습니다.
                        </PolicySection>
                        <PolicySection title="환불 정책">
                            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#1e40af', lineHeight: 1.7 }}>
                                결제 후 <b>7일 이내</b>에 서비스 사용량이 없는 경우 <b>전액 환불</b>이 가능합니다.<br/>
                                사용량이 있는 경우에는 사용 기간에 대해 일할 계산하여 환불됩니다.
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 8 }}>환불 기준</div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc' }}>
                                        <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontWeight: 600 }}>환불 시점</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontWeight: 600 }}>사용량</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontWeight: 600 }}>환불 금액</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: '#475569' }}>결제일로부터 7일 이내</td>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: '#475569' }}>사용량 없음</td>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: '#16a34a', fontWeight: 700 }}>전액 환불</td>
                                    </tr>
                                    <tr>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: '#475569' }}>결제일로부터 7일 이내</td>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: '#475569' }}>사용량 있음</td>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: '#475569' }}>일할 계산 환불</td>
                                    </tr>
                                    <tr>
                                        <td style={{ padding: '8px 12px', color: '#475569' }}>결제일로부터 7일 경과</td>
                                        <td style={{ padding: '8px 12px', color: '#94a3b8' }}>-</td>
                                        <td style={{ padding: '8px 12px', color: '#475569' }}>일할 계산 환불</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div style={{ marginTop: 14, fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 8 }}>환불 계산 방법</div>
                            <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.85 }}>
                                · 월간 구독: 환불금액 = (구독 금액 ÷ 30일) × 잔여 일수<br/>
                                · 연간 구독: 환불금액 = (구독 금액 ÷ 365일) × 잔여 일수<br/>
                                · 환불 금액은 VAT 포함 실결제 금액 기준으로 계산됩니다.<br/>
                                · 사용 일수는 구매일을 포함하여 계산됩니다.<br/>
                                · 100원 미만은 절사됩니다.
                            </div>
                            <div style={{ marginTop: 14, fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 8 }}>환불 처리</div>
                            <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.85 }}>
                                · 환불 승인 시 영업일 기준 3~5일 이내에 처리됩니다.<br/>
                                · 결제 수단에 따라 환불 기간이 달라질 수 있습니다.<br/>
                                · 토스페이먼츠를 통해 안전하게 환불 처리됩니다.
                            </div>
                            <div style={{ marginTop: 14, fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 8 }}>환불 불가 사유</div>
                            <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.85 }}>
                                · 서비스 이용약관을 위반한 경우<br/>
                                · 이미 환불이 처리된 건에 대한 중복 환불 요청
                            </div>
                        </PolicySection>
                        <PolicySection title="제7조 (기타 안내사항)">
                            <b>세금계산서:</b> 사업자 회원은 결제 후 마이페이지에서 신청 가능 (당월 결제건은 다음달 10일까지)<br/>
                            <b>결제 내역:</b> 마이페이지 {'>'} 결제 내역에서 결제일시·금액·구독권 종류 확인 가능<br/>
                            <b>잔여 한도:</b> 마이페이지 {'>'} 구독 관리에서 잔여 일수·이용 한도·사용량 실시간 확인 가능
                        </PolicySection>
                        <PolicySection title="결제 문의">
                            <b>IT 담당자 (결제 기술 문의):</b> 010-5732-1595<br/>
                            <b>고객센터 (일반 문의·환불):</b> 010-5831-1070 · tnxorrla123@naver.com<br/>
                            운영시간: 평일 09:00 ~ 18:00 (주말·공휴일 휴무)
                        </PolicySection>
                        <p style={{ marginTop: 16, fontSize: 11, color: '#94a3b8' }}>시행일: 2026년 6월 1일</p>
                    </div>
                )}
            </Modal>
            <ToastContainer
                position="top-center"
                autoClose={4000}
                newestOnTop
                closeOnClick
                pauseOnHover
                theme="light"
                limit={4}
            />
            <Drawer
                title="ADCARE"
                placement="left"
                onClose={() => setMobileDrawerOpen(false)}
                open={mobileDrawerOpen}
                styles={{ 
                    body: { padding: 0, background: '#001529' }, 
                    header: { background: '#001529', borderBottom: '1px solid #002140' },
                    title: { color: '#fff' }
                }}
                closeIcon={<span style={{ color: '#fff', fontSize: '16px' }}>✕</span>}
                width={240}
            >
                <Menu
                    theme="dark"
                    mode="inline"
                    selectedKeys={[location.pathname]}
                    items={menuItems}
                    onClick={(e) => {
                        onClickMenu(e);
                        setMobileDrawerOpen(false);
                    }}
                    style={{ borderRight: 0 }}
                />
            </Drawer>
        </Layout>
    );
};

export default MainLayout;
